import React from "react";
import type { PackagePermissions, SubscriptionPackage } from "../../types/subscription";
import { CheckCircle, ShoppingBag } from "lucide-react";
import { gh, percentSaved } from "../../lib/packageFormatters";

type Props = {
  pkg: SubscriptionPackage;
  isBest?: boolean;
  onSelect: (pkg: SubscriptionPackage) => void;
};


const PackageCard: React.FC<Props> = ({ pkg, isBest = false, onSelect }) => {
  const details = pkg.PackageDetails[0] ?? ({} as PackagePermissions);
  const styles = pkg.PackageStyles?.[0] ?? {};
  const billings = pkg.PackageBilling ?? [];

  console.log("Package: ", pkg)
  // pick monthly and yearly billing rows if present
  const monthlyBilling = billings.find((b) =>
    b.monthlyPeriod?.toLowerCase().includes("month")
  );
  const yearlyBilling = billings.find((b) =>
    b.monthlyPeriod?.toLowerCase().includes("year")
  );

  const monthlyAmount = monthlyBilling ? monthlyBilling.bill : null;
  const yearlyAmount = yearlyBilling ? yearlyBilling.bill : null;

  const savePct = percentSaved(monthlyAmount, yearlyAmount);

  // marketing benefit lines
  const benefits = [
    Number(details.itemLimit) === 0
      ? "Add an unlimited number of different products to your inventory"
      : `Add up to ${details.itemLimit} products to your inventory`,
    Number(details.monthlySalesLimit) === 0
    ? "Sell unlimited items every month"
      : `Sell up to ${details.monthlySalesLimit} items monthly to grow your revenue`,
    Number(details.employeeLimit)=== 0
      ? "Invite unlimited team members"
      : `Manage up to ${details.employeeLimit} team members with permission controls`,
    Number(details.storeLimit) === 0
      ? "Run unlimited stores from one account"
      : `Run up to ${details.storeLimit} stores and centralize operations`,
  ].filter(Boolean);

  // feature bullets for boolean capabilities (only show when true)
  const featureBullets: string[] = [];
  if (details.hasAnalytics) featureBullets.push("Unlock advanced sales & customer analytics");
  if (details.hasInventoryOverview) featureBullets.push("Real-time inventory overview across stores");
  if (details.hasNotifications) featureBullets.push("Instant notifications for sales & low stock");
  if (details.hasDemandForecasting)
    featureBullets.push("AI demand forecasting to reduce stockouts and spoilage");
  if (details.hasLoyaltyProgram) featureBullets.push("Built-in customer loyalty program tools");

  // fallback gradient if not provided
  const cardBg = styles.textColor as string;
  const textColor = styles.textColor ?? "#EDEFF7";

  return (
    <div
      className="elative w-full max-w-sm mx-auto rounded-2xl shadow-xl transform transition hover:-translate-y-1 hover:shadow-2xl overflow-hidden"
      style={{
        background: "white",
        color: textColor,
        border: `1px solid ${cardBg}`,
        flexDirection: "column",
      }}
    >
      {/* Best value ribbon */}
      {isBest && (
        <div className="absolute right-1 top-2 z-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-yellow-400/95 border border-yellow-900 px-3 py-1 text-xs font-semibold text-black shadow">
            Best value
          </div>
        </div>
      )}

      <div style={{ backgroundColor: styles.bgColor as string }}>
        <h3 className="text-2xl py-5 font-semibold" style={{ color: textColor, borderBottom: `1px solid ${textColor}` }}>
          {pkg.name}
        </h3>
      </div>
      {/* Header */}
      <div className="p-6 flex flex-col lg:h-screen">
        <div className="flex flex-col items-center gap-2 w-full">

          {/* Pricing blocks: monthly & yearly stacked */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
            {/* Monthly */}
            {/* <div className="rounded-lg bg-white px-4 py-2 text-right border-1"  style={{ color: textColor, border: `1px solid ${textColor}` }}>
              <div className="text-xs uppercase opacity-80">Monthly</div>
              <div className="mt-1 text-lg font-bold">{gh(monthlyAmount)}</div>
              <div className="text-xs opacity-80">billed {monthlyBilling?.monthlyPeriod ?? "monthly"}</div>
            </div> */}

            {/* Yearly */}
            {/* <div className="rounded-lg bg-white px-4 py-2 text-right border-1"  style={{ color: textColor, border: `1px solid ${textColor}` }}>
              <div className="flex items-center gap-2">
                <div className="text-xs uppercase opacity-85">Yearly</div>
                {savePct ? (
                  <div className="ml-2 rounded-full bg-yellow-400 px-2 py-0.5 text-xs font-semibold text-black">
                    Save {savePct}%
                  </div>
                ) : null}
              </div>
              <div className="mt-1 text-lg font-bold">{gh(yearlyAmount)}</div>
              <div className="text-xs opacity-80">billed {yearlyBilling?.monthlyPeriod ?? "yearly"}</div>
            </div> */}

                <div className="flex flex-col items-start md:items-center gap-3 w-full mb-4">
                  {/* Monthly */}
                  <div
                    className="rounded-lg bg-white px-4 py-2 text-center border-1 w-full"
                    style={{ color: textColor, border: `1px solid ${textColor}` }}
                  >
                    <div className="text-xs uppercase opacity-80">Monthly</div>
                    <div className="mt-1 text-lg font-bold">{gh(monthlyAmount)}</div>
                    <div className="text-xs opacity-80">
                      billed {monthlyBilling?.monthlyPeriod ?? "monthly"}
                    </div>
                  </div>

                  {/* Yearly */}
                  <div
                    className="rounded-lg bg-white px-4 py-2 text-center border-1 w-full"
                    style={{ color: textColor, border: `1px solid ${textColor}` }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div className="text-xs uppercase opacity-85">Yearly</div>
                      {savePct ? (
                        <div className="ml-2 rounded-full bg-yellow-400 px-2 py-0.5 text-xs font-semibold text-black">
                          Save {savePct}%
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-1 text-lg font-bold">{gh(yearlyAmount)}</div>
                    <div className="text-xs opacity-80">
                      billed {yearlyBilling?.monthlyPeriod ?? "yearly"}
                    </div>
                  </div>
                </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-4 h-px w-full opacity-25" style={{ backgroundColor: textColor }}/>

        {/* Benefit marketing lines */}
        <div className="grid gap-2">
          {benefits.map((b, i) => (
            <div key={i} className="flex gap-4 text-left text-gray-800 font-semibold opacity-95">
                <div>
                  <ShoppingBag />
                </div>
                <div>
                  {b}
                </div>
            </div>
          ))}

          {/* boolean feature bullets */}
          {featureBullets.length > 0 && (
            <div className="flex flex-col gap-2 text-left font-semibold opacity-95">
              {featureBullets.map((f, i) => (
                <div key={i} className="flex gap-2 p-2 justify-center items-center rounded-md" style={{ border: `1px solid ${cardBg}`}}>
                  <div>
                    <CheckCircle />
                  </div>
                  <div>
                    {f}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-5 lg:mt-auto flex items-center justify-center gap-4">
          <button
            onClick={() => onSelect(pkg)}
            className="w-full text-center  gap-2 rounded-full bg-primary px-4 py-4 text-sm font-semibold text-white shadow hover:opacity-95"
          >
            Select plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackageCard;
